import { Box, Typography, Button, Paper, IconButton, TextField, InputAdornment, Fab, Card } from '@mui/material'
import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useGeneratedCategoriesStore } from '../../store/categoriesStore'
import axios from 'axios'
import { useMutation } from '@tanstack/react-query'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

export const Route = createFileRoute(
  '/(admin)/_admin/admin-advanced-interview-generator-step-2',
)({
  component: RouteComponent,
})

function RouteComponent() {
    const search = useSearch({ from: '/(admin)/_admin/admin-advanced-interview-generator-step-2' });
    const navigate = useNavigate();
    const techId = (search as any).techId;
    
    // Get generated categories from store
    const { generatedCategories, setGeneratedCategories } = useGeneratedCategoriesStore();
    
    // Local state for editing categories
    const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [editingDescription, setEditingDescription] = useState('');
    const [editingQuestions, setEditingQuestions] = useState(0);

    // Log categories when component mounts or categories change
    useEffect(() => {
        console.log('Generated Categories:', generatedCategories);
        generatedCategories.forEach(category => {
            console.log(`Category ID: ${category.id}, Name: ${category.name}`);
        });
    }, [generatedCategories]);

    // Mutation for advancing to next step
    const advanceToNextStepMutation = useMutation({
        mutationFn: async () => {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                'https://interviewer.runasp.net/api/categories/advanced-generator/categories',
                {
                    categories: generatedCategories,
                    technologyId: techId
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        },
        onSuccess: (data) => {
            console.log('Successfully advanced to next step:', data);
            // Navigate to review mode
            navigate({
                to: '/admin-review-mode',
                search: {
                    techId: techId
                }
            });
        },
        onError: (error) => {
            console.error('Error advancing to next step:', error);
        }
    });

    const handleEditCategory = (category: any) => {
        setEditingCategoryId(category.id);
        setEditingName(category.name);
        setEditingDescription(category.description);
        setEditingQuestions(category.recommendedNumberOfQuestions);
    };

    const handleSaveEdit = () => {
        if (editingCategoryId) {
            const updatedCategories = generatedCategories.map(cat => 
                cat.id === editingCategoryId 
                    ? { 
                        ...cat, 
                        name: editingName, 
                        description: editingDescription, 
                        recommendedNumberOfQuestions: editingQuestions 
                    }
                    : cat
            );
            setGeneratedCategories(updatedCategories);
            setEditingCategoryId(null);
        }
    };

    const handleCancelEdit = () => {
        setEditingCategoryId(null);
    };

    const handleDeleteCategory = (categoryId: string) => {
        const updatedCategories = generatedCategories.filter(cat => cat.id !== categoryId);
        setGeneratedCategories(updatedCategories);
    };

    const handleNextClick = () => {
        if (generatedCategories.length > 0) {
            advanceToNextStepMutation.mutate();
        }
    };

    return (
        <Box sx={{maxWidth: '80%', mx: 'auto'}}>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', pt: '64px', pb: '32px'}}>
                <Typography variant='h1'>Customize your interview</Typography>
                <Typography variant='h4'>Review and modify the generated categories before proceeding</Typography>
            </Box>
            
            <Box sx={{ mb: '32px' }}>
                {generatedCategories.length === 0 ? (
                    <Typography variant='h6' sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        No categories generated yet. Please go back to step 1.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {generatedCategories.map((category) => (
                            <Card key={category.id} sx={{ p: '24px', borderRadius: '12px' }}>
                                {editingCategoryId === category.id ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                        <TextField
                                            fullWidth
                                            label="Category Name"
                                            value={editingName}
                                            onChange={(e) => setEditingName(e.target.value)}
                                        />
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Description"
                                            value={editingDescription}
                                            onChange={(e) => setEditingDescription(e.target.value)}
                                        />
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Recommended Questions"
                                            value={editingQuestions}
                                            onChange={(e) => setEditingQuestions(Number(e.target.value))}
                                            InputProps={{
                                                inputProps: { min: 1, max: 50 }
                                            }}
                                        />
                                        <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                            <Button variant="outlined" onClick={handleCancelEdit}>
                                                Cancel
                                            </Button>
                                            <Button variant="contained" onClick={handleSaveEdit}>
                                                Save
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '16px' }}>
                                            <Box>
                                                <Typography variant='h6' sx={{ mb: '8px' }}>
                                                    {category.name}
                                                </Typography>
                                                <Typography variant='body2' color="text.secondary" sx={{ mb: '8px' }}>
                                                    {category.description}
                                                </Typography>
                                                <Typography variant='body2' color="primary">
                                                    Recommended Questions: {category.recommendedNumberOfQuestions}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: '8px' }}>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleEditCategory(category)}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleDeleteCategory(category.id)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </Card>
                        ))}
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: '16px', pt: '32px' }}>
                <Button 
                    variant="outlined" 
                    onClick={() => navigate({ to: '/admin-advanced-interview-generator-step-1', search: { techId } })}
                >
                    Back
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleNextClick}
                    disabled={advanceToNextStepMutation.isPending || generatedCategories.length === 0}
                >
                    {advanceToNextStepMutation.isPending ? 'Processing...' : 'Next'}
                </Button>
            </Box>
        </Box>
    )
}
